import React, { useState, useEffect } from "react";
import {
	Box, Card, CardContent, Typography, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	Chip, Divider, IconButton, Collapse, CircularProgress,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import GroupIcon from "@mui/icons-material/Group";
import PlaceIcon from "@mui/icons-material/Place";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InventoryIcon from "@mui/icons-material/Inventory";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import KpiCard from "./KpiCard";
import HistoricoLote from "./HistoricoLote";
import ReportFilters from "./ReportFilters";
import ExportButtons from "./ExportButtons";
import { exportToPDF, exportToExcel } from "../../../utils/exportUtils";
import RelatorioDistribuidorService from "../../../service/relatorio_distribuidor_service";

const operacaoLabels = {
	repasse: "Repasse",
	consolidacao: "Consolidação",
	transformacao: "Transformação",
};

const statusLabels = {
	recebido: "Recebido",
	em_analise: "Em Análise",
	aprovado: "Aprovado",
	rejeitado: "Rejeitado",
};

function ReportRastreabilidade() {
	const [search, setSearch] = useState("");
	const [expandedLote, setExpandedLote] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [data, setData] = useState(null);

	useEffect(() => {
		loadData();
	}, [search]); // eslint-disable-line react-hooks/exhaustive-deps

	const loadData = async () => {
		try {
			setLoading(true);
			setError(null);
			const service = new RelatorioDistribuidorService();
			const result = await service.fetchRastreabilidade(search);
			setData(result);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const filtered = data?.lotes ?? [];
	const totalLotes = filtered.length;
	const totalFornecedores = new Set(filtered.flatMap((r) => r.lotesOrigem.map((lo) => lo.fornecedor))).size;
	const totalDestinos = new Set(filtered.flatMap((r) => r.saidas.map((s) => s.destino))).size;
	const totalPeso = filtered.reduce((s, r) => s + r.pesoTotalKg, 0);

	const usosDe = (r) => r.usosEmOutrasOps ?? [];
	const ordensDe = (r) => r.ordens ?? [];
	// Resumo de cada OP para a exportação: etapas e destino do produto resultante.
	const resumirOp = (op) => {
		const etapas = op.etapas.map((e) => e.nome).join(" > ");
		const destinos = op.destinos.map((d) =>
			d.tipo === "venda" ? `venda ${d.cliente} ${d.quantidadeKg} kg` : `${descreverOp(d)} ${d.quantidadeKg} kg`,
		);
		return `OP #${op.numeroOP} (${etapas})${destinos.length ? ` -> ${destinos.join(", ")}` : ""}`;
	};
	// Lote + recebido de outras OPs = vendido + usado em outras OPs + perdas + estoque
	const estoqueDe = (r) => r.pesoTotalKg + (r.pesoRecebidoOpsKg || 0) - r.pesoSaidaKg - (r.pesoUsadoOpsKg || 0) - r.perdaKg;
	const descreverOp = (o) => `OP #${o.numeroOP}${o.lote ? ` (lote ${o.lote})` : ""}`;

	const toggleExpand = (lote) => setExpandedLote((prev) => (prev === lote ? null : lote));

	const formatDate = (d) => new Date(d + (d.includes("T") ? "" : "T12:00:00")).toLocaleDateString("pt-BR");

	const getExportConfig = () => ({
		title: "Relatório de Rastreabilidade de Lotes",
		filename: "rastreabilidade-lotes",
		kpis: [
			{ label: "Lotes Consolidados", value: String(totalLotes) },
			{ label: "Fornecedores", value: String(totalFornecedores) },
			{ label: "Destinos", value: String(totalDestinos) },
			{ label: "Peso Rastreado", value: `${totalPeso.toLocaleString("pt-BR")} kg` },
		],
		headers: ["Lote Consolidado", "Produto", "Lotes Origem", "Fornecedores", "OPs (etapas e destinos)", "Data Consolidação", "Entrada (kg)", "Recebido de outras OPs (kg)", "Saída (kg)", "Usado em outras OPs (kg)", "Perda (kg)", "Destinos", "Utilizado nas OPs"],
		rows: filtered.map((r) => [
			r.loteConsolidado, r.produto,
			r.lotesOrigem.map((lo) => lo.lote).join(", "),
			r.lotesOrigem.map((lo) => lo.fornecedor).join(", "),
			ordensDe(r).map(resumirOp).join(" | "),
			formatDate(r.dataConsolidacao),
			r.pesoTotalKg, r.pesoRecebidoOpsKg || 0, r.pesoSaidaKg, r.pesoUsadoOpsKg || 0, r.perdaKg,
			r.saidas.map((s) => `${s.destino} (${s.pesoKg} kg)`).join(", "),
			usosDe(r).map((u) => `${descreverOp(u)} - ${u.etapa} (${u.quantidadeKg} kg)`).join(", "),
		]),
	});

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6, gap: 1, color: "#8e9bae" }}>
				<CircularProgress size={20} /> <Typography variant="body2">Carregando dados de rastreabilidade...</Typography>
			</Box>
		);
	}

	if (error) {
		return (
			<Box sx={{ py: 6, textAlign: "center", color: "#dc2626" }}>
				<Typography variant="body2">Erro ao carregar dados: {error}</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
			<Grid container spacing={2}>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<LinkIcon />} title="Lotes Consolidados" value={String(totalLotes)} subtitle="rastreáveis" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<GroupIcon />} title="Fornecedores" value={String(totalFornecedores)} subtitle="na cadeia" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<PlaceIcon />} title="Destinos" value={String(totalDestinos)} subtitle="pontos de entrega" />
				</Grid>
				<Grid size={{ xs: 6, md: 3 }}>
					<KpiCard icon={<SearchIcon />} title="Peso Rastreado" value={`${totalPeso.toLocaleString("pt-BR")} kg`} />
				</Grid>
			</Grid>

			<Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
				<ReportFilters searchValue={search} onSearchChange={setSearch} searchPlaceholder="Lote, produto, fornecedor ou destino..." />
				<ExportButtons onExportPDF={() => exportToPDF(getExportConfig())} onExportExcel={() => exportToExcel(getExportConfig())} />
			</Box>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
				{filtered.length === 0 && (
					<Box sx={{ py: 4, textAlign: "center", color: "#8e9bae" }}>
						<Typography variant="body2">Nenhum lote consolidado encontrado</Typography>
					</Box>
				)}
				{filtered.map((r) => {
					const isExpanded = expandedLote === r.loteConsolidado;
					return (
						<Card key={r.loteConsolidado} sx={{ borderRadius: "16px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", border: "1px solid #dfe3e8", overflow: "hidden" }}>
							{/* Summary row */}
							<Box
								onClick={() => toggleExpand(r.loteConsolidado)}
								sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.5, cursor: "pointer", "&:hover": { bgcolor: "#f9fafb" }, transition: "background 0.15s" }}
							>
								<IconButton size="small" sx={{ p: 0 }}>
									{isExpanded ? <ExpandMoreIcon sx={{ fontSize: 18, color: "#8e9bae" }} /> : <ChevronRightIcon sx={{ fontSize: 18, color: "#8e9bae" }} />}
								</IconButton>
								<Typography sx={{ fontFamily: "monospace", fontSize: "0.85rem", fontWeight: 600, color: "#1a9e8f" }}>{r.loteConsolidado}</Typography>
								<Chip label={r.produto} size="small" variant="outlined" sx={{ fontSize: "0.7rem", height: 22, borderColor: "#dfe3e8" }} />
								<Box sx={{ flex: 1 }} />
								<Typography variant="caption" sx={{ color: "#8e9bae", display: { xs: "none", sm: "inline" } }}>
									{r.lotesOrigem.length} lote(s) origem · {ordensDe(r).length} OP(s) · {r.saidas.length} saída(s){usosDe(r).length > 0 ? ` · usado em ${usosDe(r).length} OP(s)` : ""}
								</Typography>
								<Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.8rem" }}>{r.pesoTotalKg.toLocaleString("pt-BR")} kg</Typography>
							</Box>

							<Collapse in={isExpanded}>
								<Divider />
								<CardContent sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
									{/* Composição do Lote */}
									<Box>
										<Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
											<LinkIcon sx={{ fontSize: 14 }} /> Composição do Lote Consolidado
										</Typography>
										<Box sx={{ border: "1px solid #dfe3e8", borderRadius: "12px", bgcolor: "#f9fafb", p: 2 }}>
											<Grid container spacing={2} alignItems="center">
												{/* Lotes de Origem */}
												<Grid size={{ xs: 12, lg: 5 }}>
													<Typography variant="caption" sx={{ display: "block", textAlign: "center", mb: 1, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", fontSize: "0.6rem" }}>
														Microlotes de Entrada ({r.lotesOrigem.length})
													</Typography>
													<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
														{r.lotesOrigem.map((lo) => (
															<Box key={lo.lote} sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #dfe3e8", borderRadius: "8px", bgcolor: "white", p: 1.25 }}>
																<Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#1a9e8f15", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
																	<InventoryIcon sx={{ fontSize: 16, color: "#1a9e8f" }} />
																</Box>
																<Box sx={{ flex: 1, minWidth: 0 }}>
																	<Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
																		<Typography sx={{ fontFamily: "monospace", fontSize: "0.75rem", fontWeight: 600, color: "#1a9e8f" }}>{lo.lote}</Typography>
																		<Chip
																			label={statusLabels[lo.status] || lo.status}
																			size="small"
																			sx={{ fontSize: "0.6rem", height: 18, bgcolor: lo.status === "aprovado" ? "#16a34a15" : "#8e9bae15", color: lo.status === "aprovado" ? "#16a34a" : "#8e9bae" }}
																		/>
																	</Box>
																	<Typography variant="caption" sx={{ color: "#8e9bae", fontSize: "0.7rem", display: "block" }}>{lo.fornecedor} - {lo.cidadeFornecedor}</Typography>
																	<Typography variant="caption" sx={{ fontSize: "0.7rem" }}>{lo.variedade} · <b>{lo.pesoKg.toLocaleString("pt-BR")} kg</b></Typography>
																</Box>
															</Box>
														))}
													</Box>
												</Grid>

												{/* Seta */}
												<Grid size={{ xs: 12, lg: 2 }} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
													<Box sx={{ display: "flex", flexDirection: { xs: "row", lg: "column" }, alignItems: "center", gap: 0.5 }}>
														{r.lotesOrigem.length > 1 && (
															<Typography variant="caption" sx={{ fontSize: "0.6rem", color: "#8e9bae", fontWeight: 500 }}>Consolidação</Typography>
														)}
														<Box sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "#1a9e8f", display: "flex", alignItems: "center", justifyContent: "center" }}>
															<ArrowForwardIcon sx={{ fontSize: 16, color: "white" }} />
														</Box>
													</Box>
												</Grid>

												{/* Lote Consolidado */}
												<Grid size={{ xs: 12, lg: 5 }}>
													<Typography variant="caption" sx={{ display: "block", textAlign: "center", mb: 1, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", fontSize: "0.6rem" }}>
														Lote Consolidado
													</Typography>
													<Box sx={{ border: "2px solid #1a9e8f30", borderRadius: "8px", bgcolor: "white", p: 1.5 }}>
														<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
															<Box sx={{ width: 40, height: 40, borderRadius: "50%", bgcolor: "#1a9e8f15", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
																<CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#1a9e8f" }} />
															</Box>
															<Box>
																<Typography sx={{ fontFamily: "monospace", fontSize: "0.85rem", fontWeight: 700, color: "#1a9e8f" }}>{r.loteConsolidado}</Typography>
																<Typography variant="caption" sx={{ color: "#8e9bae" }}>{r.produto}</Typography>
																<Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{r.pesoTotalKg.toLocaleString("pt-BR")} kg</Typography>
															</Box>
														</Box>
														<Divider sx={{ my: 1, borderStyle: "dashed" }} />
														<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
															<Typography variant="caption" sx={{ color: "#8e9bae", fontSize: "0.7rem" }}>{r.lotesOrigem.length} lote(s) de origem</Typography>
															<Typography variant="caption" sx={{ color: "#8e9bae", fontSize: "0.7rem" }}>{new Set(r.lotesOrigem.map(lo => lo.fornecedor)).size} fornecedor(es)</Typography>
															<Typography variant="caption" sx={{ color: "#8e9bae", fontSize: "0.7rem" }}>Consolidado em {formatDate(r.dataConsolidacao)}</Typography>
														</Box>
													</Box>

													{/* Destinos */}
													{r.saidas.length > 0 && (
														<Box sx={{ mt: 1 }}>
															<Typography variant="caption" sx={{ display: "block", textAlign: "center", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", fontSize: "0.6rem", mb: 0.5 }}>
																→ Destinos ({r.saidas.length})
															</Typography>
															{r.saidas.map((s, idx) => (
																<Box key={`${s.notaFiscal}-${idx}`} sx={{ display: "flex", alignItems: "center", gap: 1, border: "1px solid #dfe3e8", borderRadius: "6px", bgcolor: "white", px: 1.25, py: 0.75, mb: 0.5 }}>
																	<LocalShippingIcon sx={{ fontSize: 14, color: "#8e9bae" }} />
																	<Typography variant="caption" sx={{ fontWeight: 500 }}>{s.destino}</Typography>
																	<Typography variant="caption" sx={{ color: "#8e9bae" }}>({s.cidade})</Typography>
																	<Box sx={{ flex: 1 }} />
																	<Typography variant="caption" sx={{ fontWeight: 600 }}>{s.pesoKg.toLocaleString("pt-BR")} kg</Typography>
																</Box>
															))}
														</Box>
													)}
												</Grid>
											</Grid>
										</Box>
									</Box>

									{/* Tabela de Lotes de Origem */}
									<Box>
										<Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
											<InventoryIcon sx={{ fontSize: 14 }} /> Detalhamento dos Lotes de Origem
										</Typography>
										<TableContainer sx={{ border: "1px solid #dfe3e8", borderRadius: "8px" }}>
											<Table size="small">
												<TableHead>
													<TableRow>
														<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Lote</TableCell>
														<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Fornecedor</TableCell>
														<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Cidade</TableCell>
														<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Variedade</TableCell>
														<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Peso (kg)</TableCell>
														<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">% do Total</TableCell>
														<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Data Entrada</TableCell>
														<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>NF</TableCell>
														<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Status</TableCell>
													</TableRow>
												</TableHead>
												<TableBody>
													{r.lotesOrigem.map((lo) => (
														<TableRow key={lo.lote + lo.notaFiscal}>
															<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem", fontWeight: 500, color: "#1a9e8f" }}>{lo.lote}</TableCell>
															<TableCell sx={{ fontSize: "0.8rem" }}>{lo.fornecedor}</TableCell>
															<TableCell sx={{ fontSize: "0.8rem", color: "#8e9bae" }}>{lo.cidadeFornecedor}</TableCell>
															<TableCell sx={{ fontSize: "0.8rem" }}>{lo.variedade}</TableCell>
															<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{lo.pesoKg.toLocaleString("pt-BR")}</TableCell>
															<TableCell align="right" sx={{ fontSize: "0.8rem", color: "#8e9bae" }}>
																{r.pesoTotalKg > 0 ? ((lo.pesoKg / r.pesoTotalKg) * 100).toFixed(1) : 0}%
															</TableCell>
															<TableCell sx={{ fontSize: "0.8rem" }}>{formatDate(lo.dataEntrada)}</TableCell>
															<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{lo.notaFiscal}</TableCell>
															<TableCell>
																<Chip
																	label={statusLabels[lo.status] || lo.status}
																	size="small"
																	sx={{ fontSize: "0.65rem", height: 20, bgcolor: lo.status === "aprovado" ? "#16a34a15" : "#8e9bae15", color: lo.status === "aprovado" ? "#16a34a" : "#8e9bae" }}
																/>
															</TableCell>
														</TableRow>
													))}
													<TableRow sx={{ bgcolor: "#f5f7f5" }}>
														<TableCell sx={{ fontSize: "0.75rem", fontWeight: 700 }} colSpan={4}>TOTAL</TableCell>
														<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700 }}>{r.pesoTotalKg.toLocaleString("pt-BR")}</TableCell>
														<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 700 }}>100%</TableCell>
														<TableCell colSpan={3} />
													</TableRow>
												</TableBody>
											</Table>
										</TableContainer>
									</Box>

									{/* Histórico do lote */}
									<Box>
										<Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
											<AccessTimeIcon sx={{ fontSize: 14 }} /> Histórico do Lote
										</Typography>
										<HistoricoLote lote={r} />
									</Box>

									{/* Saídas */}
									{r.saidas.length > 0 && (
										<Box>
											<Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
												<LocalShippingIcon sx={{ fontSize: 14 }} /> Saídas / Destinos
											</Typography>
											<TableContainer sx={{ border: "1px solid #dfe3e8", borderRadius: "8px" }}>
												<Table size="small">
													<TableHead>
														<TableRow>
															<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Destino</TableCell>
															<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Cidade</TableCell>
															<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Operação</TableCell>
															<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Qtd</TableCell>
															<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }} align="right">Peso (kg)</TableCell>
															<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>Data Saída</TableCell>
															<TableCell sx={{ fontWeight: 600, color: "#8e9bae", fontSize: "0.75rem" }}>NF</TableCell>
														</TableRow>
													</TableHead>
													<TableBody>
														{r.saidas.map((s, idx) => (
															<TableRow key={`${s.notaFiscal}-${idx}`}>
																<TableCell sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{s.destino}</TableCell>
																<TableCell sx={{ fontSize: "0.8rem", color: "#8e9bae" }}>{s.cidade}</TableCell>
																<TableCell>
																	<Chip label={operacaoLabels[s.tipoOperacao] || s.tipoOperacao} size="small" variant="outlined" sx={{ fontSize: "0.65rem", height: 20, borderColor: "#dfe3e8" }} />
																</TableCell>
																<TableCell align="right" sx={{ fontSize: "0.8rem" }}>{s.quantidade} {s.unidade}</TableCell>
																<TableCell align="right" sx={{ fontSize: "0.8rem", fontWeight: 500 }}>{s.pesoKg.toLocaleString("pt-BR")}</TableCell>
																<TableCell sx={{ fontSize: "0.8rem" }}>{formatDate(s.dataSaida)}</TableCell>
																<TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>{s.notaFiscal}</TableCell>
															</TableRow>
														))}
													</TableBody>
												</Table>
											</TableContainer>
										</Box>
									)}

									{/* Resumo do Balanço */}
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, bgcolor: "#f5f7f5", borderRadius: "8px", p: 1.5, alignItems: "center" }}>
										<Box sx={{ textAlign: "center" }}>
											<Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", fontSize: "0.6rem", display: "block" }}>Entrada</Typography>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>{r.pesoTotalKg.toLocaleString("pt-BR")} kg</Typography>
										</Box>
										{(r.pesoRecebidoOpsKg || 0) > 0 && (<>
										<ArrowForwardIcon sx={{ fontSize: 16, color: "#8e9bae" }} />
										<Box sx={{ textAlign: "center" }}>
											<Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", fontSize: "0.6rem", display: "block" }}>Recebido de outras OPs</Typography>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>+{r.pesoRecebidoOpsKg.toLocaleString("pt-BR")} kg</Typography>
										</Box>
										</>)}
										<ArrowForwardIcon sx={{ fontSize: 16, color: "#8e9bae" }} />
										<Box sx={{ textAlign: "center" }}>
											<Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", fontSize: "0.6rem", display: "block" }}>Saída</Typography>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>{r.pesoSaidaKg.toLocaleString("pt-BR")} kg</Typography>
										</Box>
										{(r.pesoUsadoOpsKg || 0) > 0 && (<>
										<ArrowForwardIcon sx={{ fontSize: 16, color: "#8e9bae" }} />
										<Box sx={{ textAlign: "center" }}>
											<Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", fontSize: "0.6rem", display: "block" }}>Usado em outras OPs</Typography>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>{r.pesoUsadoOpsKg.toLocaleString("pt-BR")} kg</Typography>
										</Box>
										</>)}
										<ArrowForwardIcon sx={{ fontSize: 16, color: "#8e9bae" }} />
										<Box sx={{ textAlign: "center" }}>
											<Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", fontSize: "0.6rem", display: "block" }}>Perda</Typography>
											<Typography variant="body2" sx={{ fontWeight: 600, color: "#dc2626" }}>
												{r.perdaKg.toLocaleString("pt-BR")} kg ({r.pesoTotalKg > 0 ? ((r.perdaKg / r.pesoTotalKg) * 100).toFixed(1) : 0}%)
											</Typography>
										</Box>
										<ArrowForwardIcon sx={{ fontSize: 16, color: "#8e9bae" }} />
										<Box sx={{ textAlign: "center" }}>
											<Typography variant="caption" sx={{ textTransform: "uppercase", letterSpacing: "0.05em", color: "#8e9bae", fontSize: "0.6rem", display: "block" }}>Estoque</Typography>
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{estoqueDe(r).toLocaleString("pt-BR")} kg
											</Typography>
										</Box>
									</Box>
								</CardContent>
							</Collapse>
						</Card>
					);
				})}
			</Box>
		</Box>
	);
}

export default ReportRastreabilidade;
